import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, LogIn, ChevronLeft, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LOGO_URL = "https://cdn.cdmx.gob.mx/identidad/cdmx/img/logo-cdmx-vertical.png";

const normalizeEmail = (value = '') => value.trim().toLowerCase();

const isValidEmail = (value = '') =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(value));

export const Login = () => {
  const navigate = useNavigate();
  const { login, user, token, loading: authLoading } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({
    email: false,
    password: false,
  });

  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  const isLoggedIn = !!user || !!token;

  useEffect(() => {
    if (!authLoading && isLoggedIn) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isLoggedIn, navigate]);

  const emailValue = normalizeEmail(loginData.email);
  const passwordValue = loginData.password;

  const emailError = useMemo(() => {
    if (!touched.email) return '';
    if (!emailValue) return 'Ingresa tu correo electrónico.';
    if (!isValidEmail(emailValue)) {
      return 'Ingresa un correo electrónico válido.';
    }
    return '';
  }, [emailValue, touched.email]);

  const passwordError = useMemo(() => {
    if (!touched.password) return '';
    if (!passwordValue.trim()) return 'Ingresa tu contraseña.';
    return '';
  }, [passwordValue, touched.password]);

  const canSubmit =
    !loading &&
    !authLoading &&
    !isLoggedIn &&
    isValidEmail(emailValue) &&
    passwordValue.trim().length > 0;

  const handleLogin = async (e) => {
    e.preventDefault();

    if (authLoading || isLoggedIn) {
      navigate('/', { replace: true });
      return;
    }

    const nextTouched = { email: true, password: true };
    setTouched(nextTouched);

    if (!emailValue || !passwordValue.trim()) {
      toast.error('Completa correo y contraseña');
      return;
    }

    if (!isValidEmail(emailValue)) {
      toast.error('Ingresa un correo electrónico válido');
      return;
    }

    if (loading) return;

    setLoading(true);
    try {
      await login(emailValue, passwordValue);
      toast.success('¡Bienvenido!');
      navigate('/', { replace: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' && detail.trim()
          ? detail
          : 'No fue posible iniciar sesión. Verifica tus credenciales.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-cover bg-center p-4"
        style={{
          backgroundImage:
            'linear-gradient(rgba(139, 28, 74, 0.85), rgba(90, 17, 48, 0.9)), url(https://cdn.cdmx.gob.mx/apps/donalap/img/wallpaper_1.jpg)',
        }}
      >
        <div className="w-full max-w-md">
          <Card className="border-none shadow-2xl">
            <CardContent className="py-10 text-center">
              <p className="text-slate-600">Verificando sesión...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoggedIn) {
    return null;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center p-4"
      style={{
        backgroundImage:
          'linear-gradient(rgba(139, 28, 74, 0.85), rgba(90, 17, 48, 0.9)), url(https://cdn.cdmx.gob.mx/apps/donalap/img/wallpaper_1.jpg)',
      }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-white/80 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver al inicio
        </Link>

        <Card className="border-none shadow-2xl">
          <CardHeader className="text-center pb-2">
            <img
              src={LOGO_URL}
              alt="Ciudad de México"
              className="h-16 mx-auto mb-4"
            />
            <CardTitle className="text-2xl font-bold text-[#8B1C4A]">
              Territorios de Paz e Igualdad
            </CardTitle>
            <CardDescription>
              Acceso a la plataforma de seguimiento territorial
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="login-email">Correo electrónico</Label>
                <Input
                  id="login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoFocus
                  placeholder="usuario@correo.com"
                  value={loginData.email}
                  onChange={(e) =>
                    setLoginData({
                      ...loginData,
                      email: e.target.value.replace(/\s+/g, ''),
                    })
                  }
                  onBlur={() =>
                    setTouched((prev) => ({ ...prev, email: true }))
                  }
                  aria-invalid={!!emailError}
                  className={emailError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                  data-testid="login-email-input"
                />
                {emailError && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {emailError}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) =>
                      setLoginData({
                        ...loginData,
                        password: e.target.value,
                      })
                    }
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, password: true }))
                    }
                    aria-invalid={!!passwordError}
                    className={`pr-10 ${passwordError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {passwordError}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-[#8B1C4A] hover:bg-[#70163A]"
                disabled={!canSubmit}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Ingresando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Iniciar Sesión
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;